/**
 * 24K.Infinity — Typography tokens
 * -------------------------------------------------------------------------
 * The reference is unambiguously Helvetica: Light / Regular / Medium only
 * (it literally labels the three weights), large editorial headlines at
 * *regular* weight with tight tracking, and giant tabular KPI numbers.
 *
 * For the web we lead with Helvetica/Helvetica Neue where the OS provides
 * it and fall back to Inter (a neutral grotesque already loaded) then Arial.
 * To match the reference pixel-for-pixel, license "Helvetica Neue" or a
 * substitute such as "Neue Haas Grotesk".
 * -------------------------------------------------------------------------
 */

export const fontFamily = {
  // Twin Console: primary = Aptos (the venue's font; present on Win11/Office),
  // alt = Helvetica / Poppins. Hierarchy comes from size/weight, not many faces.
  sans: '"Aptos", "Helvetica Neue", Helvetica, "Poppins", "Inter", Arial, "Segoe UI", sans-serif',
  // IBM Plex Mono carries every label, eyebrow, telemetry value and stat caption.
  mono: '"IBM Plex Mono", "SFMono-Regular", ui-monospace, Menlo, Consolas, monospace',
};

export const fontWeight = {
  light: 300,
  regular: 400,
  medium: 500,
  semibold: 600, // used sparingly (small labels / emphasis only)
};

// Editorial scale — px sizes, unitless line-heights, em letter-spacing.
// Big sizes carry negative tracking; small/eyebrow carry positive tracking.
export const typeScale = {
  display1: { fontSize: 64, lineHeight: 1.02, fontWeight: 400, letterSpacing: '-0.03em' },
  display2: { fontSize: 48, lineHeight: 1.04, fontWeight: 400, letterSpacing: '-0.025em' },
  // Editorial headlines lean on size + tracking, not weight (Regular/Medium only).
  h1: { fontSize: 40, lineHeight: 1.08, fontWeight: 400, letterSpacing: '-0.022em' },
  h2: { fontSize: 32, lineHeight: 1.14, fontWeight: 400, letterSpacing: '-0.02em' },
  h3: { fontSize: 24, lineHeight: 1.22, fontWeight: 500, letterSpacing: '-0.014em' },
  h4: { fontSize: 20, lineHeight: 1.3, fontWeight: 500, letterSpacing: '-0.01em' },
  h5: { fontSize: 17, lineHeight: 1.38, fontWeight: 500, letterSpacing: '-0.004em' },
  h6: { fontSize: 15, lineHeight: 1.45, fontWeight: 500, letterSpacing: '0em' },
  subtitle1: { fontSize: 16, lineHeight: 1.5, fontWeight: 500, letterSpacing: '0em' },
  subtitle2: { fontSize: 13.5, lineHeight: 1.5, fontWeight: 500, letterSpacing: '0em' },
  body1: { fontSize: 15, lineHeight: 1.6, fontWeight: 400, letterSpacing: '0em' },
  body2: { fontSize: 13.5, lineHeight: 1.58, fontWeight: 400, letterSpacing: '0em' },
  caption: { fontSize: 12, lineHeight: 1.45, fontWeight: 400, letterSpacing: '0.01em' },
  // The signature section eyebrow: MONO small caps, generously tracked.
  overline: {
    fontSize: 11,
    lineHeight: 1.4,
    fontWeight: 500,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    fontFamily: fontFamily.mono,
  },
  button: { fontSize: 14, lineHeight: 1.2, fontWeight: 500, letterSpacing: '0.005em', textTransform: 'none' },
};

// KPI / stat numbers: light weight, tabular figures, very tight tracking.
export const numeric = {
  fontFeatureSettings: '"tnum" 1, "lnum" 1',
  fontVariantNumeric: 'tabular-nums lining-nums',
  // Oversized KPIs are airy/Light in the reference (needs the Light face / Inter 300).
  display: { fontSize: 56, lineHeight: 1.0, fontWeight: 300, letterSpacing: '-0.03em' },
  large: { fontSize: 40, lineHeight: 1.0, fontWeight: 300, letterSpacing: '-0.025em' },
  medium: { fontSize: 28, lineHeight: 1.05, fontWeight: 400, letterSpacing: '-0.02em' },
};

const typography = { fontFamily, fontWeight, typeScale, numeric };
export default typography;
